/**
 * UI feature flags (SR1-flag, SR2-5, SR3-4, ENG-9, DES-6).
 *
 * The server resolves them and /auth/me returns
 *   ui_flags: { ui_refonte, call_page, kill, org: { ui_refonte, call_page } }
 * where `kill` is the global kill switch (AdminSettings.ui_refonte_kill) and
 * `org` the organisation's stored setting. The client recomputes the result
 * from those inputs so a development build can override it locally:
 *   1. kill → everything off, whatever else is set;
 *   2. the dev override (localStorage, dev builds only) beats the org setting;
 *   3. call_page needs ui_refonte (the child needs its parent).
 * A production build ignores the override. Missing or non-boolean values
 * read as false. The result is frozen for the life of the page (DES-6):
 * a later /auth/me never changes it; a full reload does.
 */
export interface UiFlags { ui_refonte: boolean; call_page: boolean }

export interface ServerUiFlags {
  kill?: unknown;
  org?: { ui_refonte?: unknown; call_page?: unknown } | null;
}

export const FLAG_OVERRIDE_KEY = 'xdial_ui_flags_override';
export const FLAGS_OFF: UiFlags = Object.freeze({ ui_refonte: false, call_page: false });

const bool = (v: unknown) => v === true;

export function resolveUiFlags(
  server: ServerUiFlags | null | undefined,
  override: Partial<Record<keyof UiFlags, unknown>> | null | undefined,
  isDevBuild: boolean,
): UiFlags {
  if (bool(server?.kill)) return FLAGS_OFF;
  const org = { ui_refonte: bool(server?.org?.ui_refonte), call_page: bool(server?.org?.call_page) };
  const o = isDevBuild && override ? override : null;
  const parent = o && 'ui_refonte' in o ? bool(o.ui_refonte) : org.ui_refonte;
  const child = o && 'call_page' in o ? bool(o.call_page) : org.call_page;
  return { ui_refonte: parent, call_page: parent && child };
}

export function readOverride(): Partial<Record<keyof UiFlags, unknown>> | null {
  try {
    const raw = localStorage.getItem(FLAG_OVERRIDE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<keyof UiFlags, unknown>>) : null;
  } catch {
    return null;
  }
}

let frozen: UiFlags | null = null;

/** Called once with the first /auth/me answer; later calls keep the first result. */
export function freezeUiFlags(server: ServerUiFlags | null | undefined): UiFlags {
  if (!frozen) frozen = resolveUiFlags(server, readOverride(), import.meta.env.DEV);
  return frozen;
}

/** The flags of this page load; off until /auth/me answered. */
export function getUiFlags(): UiFlags {
  return frozen ?? FLAGS_OFF;
}

export function _resetUiFlagsForTest(): void {
  frozen = null;
}
